/**
 * @file
 * Dish photo popups for the all-inclusive menu (PBC-819).
 *
 * Adds a camera icon next to any dish heading that has photos, and opens a
 * lightbox on click. Dishes with several photos (for example Shrimp
 * Creations, which has five preparations) get a labelled thumbnail strip.
 *
 * The map is built server side in the current language, so the Spanish page
 * matches Spanish dish names. Matching is done on a normalised heading string
 * because the menu copy contains stray &nbsp; and inconsistent spacing.
 */
(function (Drupal, once) {
  "use strict";

  function normalize(str) {
    return (str || "")
      .normalize("NFKC")
      .replace(/ /g, " ")
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9À-ɏ ]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildIndex(dishes) {
    var index = {};
    Object.keys(dishes || {}).forEach(function (name) {
      var key = normalize(name);
      if (key) {
        index[key] = dishes[name];
      }
    });
    return index;
  }

  // Nearest preceding section heading (the menu renders sections as h3).
  function sectionOf(heading) {
    var all = document.querySelectorAll("h3, h6");
    var seen = "";
    for (var i = 0; i < all.length; i++) {
      if (all[i] === heading) {
        return seen;
      }
      if (all[i].tagName === "H3") {
        seen = all[i].textContent;
      }
    }
    return seen;
  }

  var lightbox = null;
  var lastFocus = null;

  function closeLightbox() {
    if (!lightbox) {
      return;
    }
    lightbox.el.hidden = true;
    document.body.classList.remove("has-dish-lightbox");
    if (lastFocus) {
      lastFocus.focus();
    }
  }

  function ensureLightbox(settings) {
    if (lightbox) {
      return lightbox;
    }
    var el = document.createElement("div");
    el.className = "dish-lightbox";
    el.hidden = true;
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.innerHTML =
      '<div class="dish-lightbox__backdrop" data-close></div>' +
      '<div class="dish-lightbox__panel">' +
      '<button type="button" class="dish-lightbox__close" data-close></button>' +
      '<h2 class="dish-lightbox__title"></h2>' +
      '<div class="dish-lightbox__stage"><img alt=""></div>' +
      '<div class="dish-lightbox__thumbs"></div>' +
      "</div>";
    el.querySelector(".dish-lightbox__close").textContent = settings.closeLabel || "Close";
    el.querySelector(".dish-lightbox__close").setAttribute("aria-label", settings.closeLabel || "Close");
    document.body.appendChild(el);

    el.addEventListener("click", function (e) {
      if (e.target.hasAttribute("data-close")) {
        closeLightbox();
      }
    });
    document.addEventListener("keydown", function (e) {
      if (el.hidden) {
        return;
      }
      if (e.key === "Escape") {
        closeLightbox();
      }
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        var thumbs = el.querySelectorAll(".dish-lightbox__thumb");
        if (thumbs.length < 2) {
          return;
        }
        var current = el.querySelector(".dish-lightbox__thumb.is-active");
        var i = Array.prototype.indexOf.call(thumbs, current);
        var next = e.key === "ArrowRight" ? (i + 1) % thumbs.length : (i - 1 + thumbs.length) % thumbs.length;
        thumbs[next].click();
      }
    });

    lightbox = {
      el: el,
      img: el.querySelector(".dish-lightbox__stage img"),
      title: el.querySelector(".dish-lightbox__title"),
      thumbs: el.querySelector(".dish-lightbox__thumbs")
    };
    return lightbox;
  }

  function openLightbox(settings, dishName, photos, trigger) {
    var lb = ensureLightbox(settings);
    lastFocus = trigger;
    lb.title.textContent = dishName;
    lb.thumbs.innerHTML = "";

    function show(i) {
      lb.img.src = photos[i].src;
      lb.img.alt = photos[i].alt || dishName;
      Array.prototype.forEach.call(lb.thumbs.children, function (t, n) {
        t.classList.toggle("is-active", n === i);
        t.setAttribute("aria-pressed", n === i ? "true" : "false");
      });
    }

    // Only show the strip when there is a real choice to make. Two angles of
    // the same dish share a label, so number those instead.
    if (photos.length > 1) {
      var labels = photos.map(function (p) { return p.label; });
      var allSame = labels.every(function (l) { return l === labels[0]; });
      // Number any label that repeats, so two shots of the same preparation
      // are still distinguishable in the strip.
      var seen = {};
      var counts = labels.reduce(function (acc, l) { acc[l] = (acc[l] || 0) + 1; return acc; }, {});
      photos.forEach(function (p, i) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "dish-lightbox__thumb";
        b.innerHTML = '<img alt="" src="' + p.src + '">';
        var cap = document.createElement("span");
        if (allSame) {
          cap.textContent = String(i + 1);
        }
        else if (counts[p.label] > 1) {
          seen[p.label] = (seen[p.label] || 0) + 1;
          cap.textContent = p.label + " " + seen[p.label];
        }
        else {
          cap.textContent = p.label;
        }
        b.appendChild(cap);
        b.addEventListener("click", function () { show(i); });
        lb.thumbs.appendChild(b);
      });
    }

    show(0);
    lb.el.hidden = false;
    document.body.classList.add("has-dish-lightbox");
    lb.el.querySelector(".dish-lightbox__close").focus();
  }

  Drupal.behaviors.pbcMenuDishPhotos = {
    attach: function (context, settings) {
      var config = (settings && settings.pbcMenuDishPhotos) || {};
      var index = buildIndex(config.dishes);
      if (!Object.keys(index).length) {
        return;
      }

      // Dish names are not unique across the menu: in Spanish the pizza
      // "Margherita" becomes "Margarita", which also names two cocktails, so
      // the stored section decides which heading gets the photo.
      //
      // A dish can legitimately sit in more than one section though - the
      // shrimp dishes appear under both Appetizers and Seafood - so every
      // section stored for a name is considered, not just the first.
      //
      // The check also fails OPEN. If none of a dish's stored sections appear
      // anywhere on the page, the icon is attached to every match rather than
      // none. Stored sections can drift from what the matcher reads, and the
      // right failure is a photo in an extra place, not a photo nowhere.
      //
      // "Section" here means the nearest preceding h3, the menu's section
      // divider. The menu also has h2 sub-headings within a section
      // ("Highlights" sits inside "Appetizers") which are deliberately not
      // treated as sections. PBC-824 happened because the original import
      // stored the nearer h2 while this reads the h3. See PBC-824.
      var headings = document.querySelectorAll("h6");
      var nameCounts = {};
      var pageSections = {};
      headings.forEach(function (h) {
        var k = normalize(h.textContent);
        if (index[k]) {
          nameCounts[k] = (nameCounts[k] || 0) + 1;
        }
      });
      document.querySelectorAll("h3").forEach(function (h) {
        pageSections[normalize(h.textContent)] = true;
      });

      // Sections stored against each dish name, flattened across its photos.
      function sectionsFor(photos) {
        var out = [];
        photos.forEach(function (p) {
          var list = Array.isArray(p.sections) ? p.sections : (p.section ? [p.section] : []);
          list.forEach(function (s) {
            var n = normalize(s);
            if (n && out.indexOf(n) === -1) {
              out.push(n);
            }
          });
        });
        return out;
      }

      once("pbc-dish-photo", "h6", context).forEach(function (heading) {
        var key = normalize(heading.textContent);
        var photos = index[key];
        if (!photos || !photos.length) {
          return;
        }
        if (nameCounts[key] > 1) {
          var wanted = sectionsFor(photos);
          // Only enforce the section when at least one of the stored values
          // actually exists on the page; otherwise fall through and attach.
          var usable = wanted.filter(function (s) { return pageSections[s]; });
          if (usable.length && usable.indexOf(normalize(sectionOf(heading))) === -1) {
            return;
          }
        }
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "dish-photo-toggle";
        btn.setAttribute("aria-label", (config.iconLabel || "View photo") + ": " + heading.textContent.trim());
        btn.addEventListener("click", function () {
          openLightbox(config, heading.textContent.trim(), photos, btn);
        });
        heading.appendChild(btn);
        heading.classList.add("has-dish-photo");
      });
    }
  };
})(Drupal, once);
